import { Injectable } from '@angular/core';

interface SnapshotEnvelope<T> {
  version: 1;
  savedAt: number;
  value: T;
}

/**
 * Small, best-effort cache for public UI snapshots.
 *
 * It is deliberately not used as an authority for prices, inventory,
 * balances, payments, or authentication. Callers render this snapshot first
 * and still revalidate against the API every time they enter the screen.
 */
@Injectable({ providedIn: 'root' })
export class ClientSnapshotCacheService {
  private readonly storagePrefix = 'laniakea.ui.snapshot.v1:';

  key(namespace: string, scope: unknown = ''): string {
    const serialized = this.stableSerialize(scope);
    let hash = 2166136261;
    for (let index = 0; index < serialized.length; index += 1) {
      hash ^= serialized.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return `${this.storagePrefix}${namespace}:${(hash >>> 0).toString(36)}`;
  }

  get<T>(key: string, maxAgeMs: number): T | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const envelope = JSON.parse(raw) as SnapshotEnvelope<T>;
      if (envelope?.version !== 1 || !Number.isFinite(envelope.savedAt)) {
        localStorage.removeItem(key);
        return null;
      }
      if (Date.now() - envelope.savedAt > maxAgeMs) {
        localStorage.removeItem(key);
        return null;
      }
      return envelope.value ?? null;
    } catch {
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const envelope: SnapshotEnvelope<T> = { version: 1, savedAt: Date.now(), value };
      localStorage.setItem(key, JSON.stringify(envelope));
    } catch {
      // Quota/security/private-mode failures must never break the live API.
    }
  }

  private stableSerialize(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) || '';
    if (Array.isArray(value)) return `[${value.map(item => this.stableSerialize(item)).join(',')}]`;
    return `{${Object.keys(value as Record<string, unknown>).sort().map(key =>
      `${JSON.stringify(key)}:${this.stableSerialize((value as Record<string, unknown>)[key])}`
    ).join(',')}}`;
  }
}
